import type { SearchFilterField } from '@/shared/search'

import type { CustomFieldDefinition } from './types'
import { groupCustomFields } from './values'

/** The API's filter field for a custom field: `customFields.{field id}` (`CustomFieldFilters`). */
export function customFieldFilterId(fieldId: string): string {
  return `customFields.${fieldId}`
}

/**
 * The Filters panel's fields for the enabled custom fields, in the order the form draws them,
 * each labeled by its name: text and numbers by value, a person by name, a checkbox as checked or
 * not, and a dropdown or checkbox list by its values. Add them to a search definition's
 * `filters`, and allow `CustomFieldFilters.AnyField` on its endpoint.
 */
export function customFieldFilters(definitions: readonly CustomFieldDefinition[]): readonly SearchFilterField[] {
  return groupCustomFields(definitions)
    .flatMap((group) => group.fields)
    .map((field): SearchFilterField => {
      const id = customFieldFilterId(field.id)
      const label = field.name
      switch (field.type) {
        case 'text':
          return { id, label, kind: 'text', nullable: true }
        case 'number':
          return { id, label, kind: 'number', nullable: true, integer: true }
        case 'person':
          return { id, label, kind: 'person', nullable: true }
        case 'checkbox':
          return {
            id,
            label,
            kind: 'choice',
            options: [
              { value: 'true', labelId: 'customFields.filter.checked' },
              { value: 'false', labelId: 'customFields.filter.unchecked' },
            ],
          }
        case 'dropdown':
        case 'checkboxList':
          return {
            id,
            label,
            kind: 'choice',
            nullable: true,
            options: field.listValues.map((item) => ({ value: item.value, label: item.value })),
          }
      }
    })
}
